import React from "react";
import { cn } from "~/lib/utils";
import { SyntaxHighlighterLanguages } from "./types";

// // // //

interface Props {
    code: string;
    className?: string;
    language: SyntaxHighlighterLanguages;
    showLineNumbers?: boolean;
}

/**
 * SyntaxHighlighter
 * Renders a code snippet with syntax highlighting
 */
export function SyntaxHighlighter(props: Props) {
    const { code, language, className, showLineNumbers = false } = props;

    if (showLineNumbers) {
        const lines = code.split("\n");
        return (
            <pre
                data-language={language}
                className={cn(`language-${language}`, className)}
            >
                <code>
                    {lines.map((line, i) => (
                        <div key={i} className="flex">
                            <span className="text-slate-500 select-none w-8 text-right mr-4 shrink-0">
                                {i + 1}
                            </span>
                            <span>{highlightSyntax(line, language)}</span>
                        </div>
                    ))}
                </code>
            </pre>
        );
    }

    return (
        <pre data-language={language} className={cn(className)}>
            <code className="font-mono">{highlightSyntax(code, language)}</code>
        </pre>
    );
}

// // // //

function highlightSyntax(code: string, language: string): React.ReactNode {
    if (language === "bash" || language === "curl") {
        return code.split(/(\s+)/).map((part, i) => {
            if (part.startsWith("curl") || part.startsWith("--")) {
                return (
                    <span key={i} className="text-[#7dd3fc]">
                        {part}
                    </span>
                );
            }
            if (part.startsWith("'") || part.startsWith('"')) {
                return (
                    <span key={i} className="text-[#86efac]">
                        {part}
                    </span>
                );
            }
            if (part.startsWith("https://") || part.startsWith("http://")) {
                return (
                    <span key={i} className="text-[#fde68a]">
                        {part}
                    </span>
                );
            }
            return part;
        });
    }

    if (
        language === "javascript" ||
        language === "js" ||
        language === "typescript" ||
        language === "ts"
    ) {
        // Handle comments — match leading whitespace + // rest of line
        const commentMatch = code.match(/^(\s*)(\/\/.*)$/);
        if (commentMatch) {
            return (
                <>
                    {commentMatch[1]}
                    <span className="text-slate-500">{commentMatch[2]}</span>
                </>
            );
        }

        // Extract strings and template literals as whole tokens first,
        // then split remaining code by separators
        const tokens = code.split(
            /("(?:[^"\\]|\\.)*"|'(?:[^'\\]|\\.)*'|`(?:[^`\\]|\\.)*`|\s+|[{}()[\]:,;])/g,
        );

        return tokens.map((part, i) => {
            if (!part) return null;
            if (
                [
                    "const",
                    "let",
                    "var",
                    "async",
                    "await",
                    "function",
                    "return",
                    "import",
                    "from",
                    "export",
                    "type",
                    "interface",
                    "enum",
                    "as",
                    "implements",
                    "extends",
                    "readonly",
                    "keyof",
                    "typeof",
                    "new",
                ].includes(part)
            ) {
                return (
                    <span key={i} className="text-[#c084fc]">
                        {part}
                    </span>
                );
            }
            if (
                part.startsWith("'") ||
                part.startsWith('"') ||
                part.startsWith("`")
            ) {
                return (
                    <span key={i} className="text-[#86efac]">
                        {part}
                    </span>
                );
            }
            if (/^\d+$/.test(part)) {
                return (
                    <span key={i} className="text-[#fde68a]">
                        {part}
                    </span>
                );
            }
            return part;
        });
    }

    if (language === "python") {
        const pyCommentMatch = code.match(/^(\s*)(#.*)$/);
        if (pyCommentMatch) {
            return (
                <>
                    {pyCommentMatch[1]}
                    <span className="text-slate-500">{pyCommentMatch[2]}</span>
                </>
            );
        }

        const tokens = code.split(
            /("(?:[^"\\]|\\.)*"|'(?:[^'\\]|\\.)*'|f"(?:[^"\\]|\\.)*"|f'(?:[^'\\]|\\.)*'|\s+|[{}()[\]:,])/g,
        );

        return tokens.map((part, i) => {
            if (!part) return null;
            if (
                [
                    "import",
                    "from",
                    "def",
                    "class",
                    "return",
                    "if",
                    "else",
                    "elif",
                    "for",
                    "in",
                    "while",
                    "True",
                    "False",
                    "None",
                ].includes(part)
            ) {
                return (
                    <span key={i} className="text-[#c084fc]">
                        {part}
                    </span>
                );
            }
            if (
                part.startsWith("'") ||
                part.startsWith('"') ||
                part.startsWith('f"') ||
                part.startsWith("f'")
            ) {
                return (
                    <span key={i} className="text-[#86efac]">
                        {part}
                    </span>
                );
            }
            if (/^\d+$/.test(part)) {
                return (
                    <span key={i} className="text-[#fde68a]">
                        {part}
                    </span>
                );
            }
            return part;
        });
    }

    return code;
}
